import { createTheme, adaptV4Theme, Theme, responsiveFontSizes } from '@mui/material/styles'
import { FixedInitialArrayLength } from '~utils/types/FixedInitialArrayLength'

interface CustomMuiStyles {
  mainContentMaxWidth: number
  drawerWidthSmall: number
  drawerWidthLarge: number
  drawerIconHeight: number
  boxShadow: FixedInitialArrayLength<string, 5>
}

// extend Theme type with custom styles
declare module '@mui/material/styles' {
  interface Theme {
    custom?: CustomMuiStyles
  }
  // allow configuration using `createMuiTheme`
  interface ThemeOptions {
    custom?: CustomMuiStyles
  }
}

const defaultTheme = createTheme()
const v4AddaptedTheme = adaptV4Theme({
  typography: {
    fontFamily: [
      'Calibri Bold',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: `${Math.pow(1.333, 4)}rem`,
    },
    h2: {
      fontSize: `${Math.pow(1.333, 3)}rem`,
    },
    h3: {
      fontSize: `${Math.pow(1.333, 2)}rem`,
    },
    h4: {
      fontSize: `${Math.pow(1.333, 1)}rem`,
    },
    h5: {
      fontSize: `${Math.pow(1.333, 1)}rem`,
    },
    h6: {
      fontSize: `${Math.pow(1.333, 1)}rem`,
    },
  },
  palette: {
    // mode: 'dark', // are these light or dark mode styles?
    background: {
      default: '#f5f5f6',
    },
    primary: {
      main: '#1B1B1D',
      light: '#69a8ff',
      dark: '#004fcb',
    },
    secondary: {
      main: '#f5a623',
      light: '#393a3d',
      // dark: '#000000',
    },
    error: {
      main: '#f44336',
      light: '#fed9cc',
    },
    // ToDo -- grey dosnt seem to have these props
    // grey: {
    //   main: '#f5f5f6',
    //   light: '#ffffff',
    //   dark: '#e1e2e1',
    // },
  },
  overrides: {
    MuiCssBaseline: {
      '@global': {
        body: {
          overflow: 'hidden',
        },
      },
    },
    MuiTab: {
      root: {
        flexDirection: 'row',
      },
      wrapped: {
        minHeight: defaultTheme.spacing(6),
      },
    },
  },
  breakpoints: {
    values: {   // defaults
      xs: 470,  // 0
      sm: 600,  // 600
      md: 960,  // 960
      lg: 1280, // 1280
      xl: 1920, // 1920
    },
  },
})

const themeBase = createTheme({
  ...v4AddaptedTheme,
  components: {
    ...v4AddaptedTheme.components,
    MuiUseMediaQuery: {
      defaultProps: {
        noSsr: true,
      },
    },
  },
  custom: {
    mainContentMaxWidth: 1680,
    drawerWidthSmall: parseInt(defaultTheme.spacing(7)),
    drawerWidthLarge: 305, // previous 270
    drawerIconHeight: 21,
    boxShadow: [
      '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
      '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
      '0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)',
      '0 14px 28px rgba(0,0,0,0.25), 0 10px 10px rgba(0,0,0,0.22)',
      '0 19px 38px rgba(0,0,0,0.30), 0 15px 12px rgba(0,0,0,0.22)',
    ],
  },
})

export const theme: Theme = responsiveFontSizes(themeBase, {
// breakpoints: (Array<String> [optional]): Default to ['sm', 'md', 'lg']. Array of breakpoints (identifiers).
// disableAlign: (Boolean [optional]): Default to false. Whether font sizes change slightly so line heights are preserved and align to
//                Material Design's 4px line height grid. This requires a unitless line height in the theme's styles.
// factor: (Number [optional]): Default to 2. This value determines the strength of font size resizing. The higher the value, the less
//          difference there is between font sizes on small screens. The lower the value, the bigger font sizes for small screens.
//          The value must be greater than 1.
// variants: (Array<String> [optional]): Default to all. The typography variants to handle.
})
